import { useEffect, useRef, useCallback } from 'react';
import { createFractalWorkerClient } from './fractalWorkerPool';

/** Share a lazy CPU worker pool across canvases, including across concurrent renders. */
export function useFractalWorker() {
  const clientRef = useRef(null);

  useEffect(() => {
    const client = createFractalWorkerClient();
    clientRef.current = client;
    return () => {
      clientRef.current = null;
      client.release();
    };
  }, []);

  const renderFull = useCallback(async (params, control) => {
    if (!clientRef.current) throw new Error('Worker client not mounted');
    return control === undefined
      ? clientRef.current.renderFull(params)
      : clientRef.current.renderFull(params, control);
  }, []);

  const renderPan = useCallback(async (params) => {
    if (!clientRef.current) throw new Error('Worker client not mounted');
    return clientRef.current.renderPan(params);
  }, []);

  // Availability checks never create workers; rendering does that on demand.
  const isWorkerAvailable = useCallback(() => clientRef.current?.isWorkerAvailable() || false, []);
  const shouldUseWorker = useCallback((width, height) =>
    clientRef.current?.shouldUseWorker(width, height) || false, []);

  return { renderFull, renderPan, isWorkerAvailable, shouldUseWorker };
}

/**
 * Parse a colorFunc to extract serializable parameters for the worker
 * Since functions can't be sent to workers, we need to serialize them
 */
export function serializeColorFunc(colorFunc) {
  if (colorFunc.colorParams) return { ...colorFunc.colorParams };

  // Get the function source to analyze it
  const funcStr = colorFunc.toString();
  
  // Default values
  const params = {
    r: 255,
    gScale: 7,
    b: 0,
    aScale: 15,
    aFixed: null,
    useMu: true
  };

  // Check if it uses numIterations instead of mu
  if (funcStr.includes('numIterations')) {
    params.useMu = false;
  }

  // Extract multipliers using regex
  // Pattern: mu * N or numIterations * N
  const gScaleMatch = funcStr.match(/(?:mu|numIterations)\s*\*\s*(\d+)/);
  if (gScaleMatch) {
    params.gScale = parseInt(gScaleMatch[1], 10);
  }

  // Look for alpha scale - usually the second multiplier or a fixed value
  // Pattern: [ 255, ... , 0, mu * N ] or [ 255, ..., 0, 255 ]
  const alphaMatches = funcStr.match(/,\s*(?:(\d+)|(?:mu|numIterations)\s*\*\s*(\d+))\s*\]/);
  if (alphaMatches) {
    if (alphaMatches[1]) {
      // Fixed alpha value
      params.aFixed = parseInt(alphaMatches[1], 10);
    } else if (alphaMatches[2]) {
      // Alpha scale
      params.aScale = parseInt(alphaMatches[2], 10);
    }
  }

  return params;
}
